import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { REQUEST_ERRORS_LISTS } from "../../constants/requestErrorFlags"

import { showSnackBar } from "../../components/Snackbar/Snackbar"
import { useMyContextLoading } from "../../components/LoadingContextProvider/LoadingContextProvider"
import { getListCache, setListCache, buildCacheKey } from "../../utils/listCache"
import Table from "./Table"
import { getAllHistories, getAllHistoriesByUserId, downloadHistoryCSV } from "../../features/history/history"
import TableFooter from "../../components/Pagination/TableFooter"
import { initQueryData } from "../../components/Filters/helper"
import Filters from "../../components/Filters/Filters"
import Button from "../../components/Button/Button"
import PageTitle from "../../components/PageTitle/PageTitle"
import AppliedFilters from "../../components/AppliedFilters/AppliedFilters"
import useURLState from "../../hooks/useURLState"


const sortURLConfig = {
  sortField: { type: 'string', default: '' },
  sortOrder: { type: 'string', default: '' },
}

const History = ({ userId = null, historyPage = false }) => {
  const dispatch = useDispatch()
  const listStrict = useSelector(selectFeatureFlag(REQUEST_ERRORS_LISTS))
  const listStrictRef = useRef(listStrict)
  listStrictRef.current = listStrict
  const { triggerLoading, triggerBar } = useMyContextLoading()
  const [reqError, setReqError] = useState('')
  const [history, setHistory] = useState([])
  const [valuePagination, setValuePagination] = useState(10)
  const [pagePagination, setPagePagination] = useState(1)
  const [pageCount, setPageCount] = useState('')
  const [totalCount, setTotalCount] = useState(null)
  const [requestFilters, setRequestFilters] = useState([initQueryData])
  const [filterData, setFilterData] = useState({ filters: [], utils: [] })
  const [fetchTrigger, setFetchTrigger] = useState(0)

  const urlState = useURLState(historyPage ? sortURLConfig : {})
  const sortField = historyPage ? urlState.sortField : ''
  const sortOrder = historyPage ? urlState.sortOrder : ''

  const handleSort = (field, order) => {
    urlState.setMultiple({ sortField: field, sortOrder: order })
  }

  const applyFilters = (newFilters) => {
    setRequestFilters(newFilters)
    setFetchTrigger(p => p + 1)
  }

  useEffect(() => {
    const handleReqError = () => {
      reqError?.length > 0 && showSnackBar("error", reqError)
      setReqError('')
    }

    handleReqError()
  }, [reqError])

  const getUserHistory = async () => {
    const cacheKey = buildCacheKey('history', { userId, historyPage, valuePagination, pagePagination, sortField, sortOrder, requestFilters })
    const cached = getListCache(cacheKey)
    if (cached) {
      setHistory(cached.history)
      setPageCount(cached.pageCount)
      setTotalCount(cached.totalCount)
    }
    triggerBar(true)
    try {

      const offset = (pagePagination - 1) * valuePagination
      const hasFilters = requestFilters?.some(f => f.filterType?.length > 0)
      let filters = hasFilters ? requestFilters : ""

      let response
      if (userId && !historyPage) {
        response = await dispatch(getAllHistoriesByUserId({
          userId,
          offset,
          limit: valuePagination,
          filters,
          sortField,
          sortOrder,
        }))
      }
      if (historyPage && !userId) {
        response = await dispatch(getAllHistories({
          userId,
          offset,
          limit: valuePagination,
          filters,
          sortField,
          sortOrder,
        }))
      }

      if (response.payload && response.payload.data) {
        const fresh = {
          history: response?.payload?.data?.history,
          pageCount: response?.payload?.data?.meta?.pageCount,
          totalCount: response?.payload?.data?.meta?.total,
        }
        setHistory(fresh.history)
        setPageCount(fresh.pageCount)
        setTotalCount(fresh.totalCount)
        setListCache(cacheKey, fresh)
      } else {
        if (listStrictRef.current && response?.error) {
          showSnackBar("error", typeof response.payload === 'string' ? response.payload : 'Failed to fetch history')
        }
        console.error('No data received from server')
      }
    } catch (error) {
      setReqError(error)
      console.error('An unexpected error occurred:', error)
    }
    triggerBar(false)
  }

  const getHistoryCSVFunc = async () => {
    triggerLoading(true)
    try {
      const hasFilters = requestFilters?.some(f => f.filterType?.length > 0)
      let filters = hasFilters ? requestFilters : ""
      const response = await dispatch(downloadHistoryCSV({ filters }))
      if (listStrictRef.current && response?.error) {
        showSnackBar("error", typeof response.payload === 'string' ? response.payload : 'Failed to export history')
      }
    } catch (error) {
      console.error('Error downloading CSV file:', error)
    }
    triggerLoading(false)
  }

  useEffect(() => {
    getUserHistory()
  }, [])

  useEffect(() => {
    getUserHistory()
  }, [valuePagination, pagePagination, sortField, sortOrder, fetchTrigger])

  useEffect(() => {
    setPagePagination(1)
  }, [valuePagination])

  return (
    <div className="flex flex-col w-full">
      <div className="flex justify-between items-center px-5">
        <PageTitle title="History" />
        <Button type={"outlined"} onClick={getHistoryCSVFunc} classes={"border !border-primary-main h-10"}>
          <p className={`flex`}> Export In CSV </p>
        </Button>
      </div>
      <div className="flex flex-row justify-between items-center px-5 py-4">
        <Filters isFilterList={false} fetchByFilters={applyFilters}
          requestFilters={requestFilters}
          flow={historyPage ? "history" : 'singleHistory'}
          onFilterDataLoaded={setFilterData} />
      </div>
      <div className="px-5 mb-4">
        <AppliedFilters
          requestFilters={requestFilters}
          filterDefs={filterData.filters} utilDefs={filterData.utils}
          onRemove={(index) => {
            const active = requestFilters.filter(f => f.fieldName || f.operator || f.value)
            const updated = active.filter((_, i) => i !== index)
            setRequestFilters(updated.length > 0 ? updated : [initQueryData])
            setFetchTrigger(p => p + 1)
          }}
          onClearAll={() => {
            setRequestFilters([initQueryData])
            setFetchTrigger(p => p + 1)
          }}
        />
      </div>
      <div className="px-5">
        <Table
          curUserId={userId}
          history={history}
          setHistory={setHistory}
          historyPage={historyPage}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={historyPage ? handleSort : undefined}
        />
      </div>
      <TableFooter page={pagePagination} limit={valuePagination} total={totalCount}
        pageCount={pageCount} setPage={setPagePagination} setLimit={setValuePagination} />
    </div>
  )
}
export default History
